
import { createContext, useContext, useCallback, useEffect, useRef } from "react";
import { useLocalStorage } from "@mantine/hooks";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useLocalStorage<Theme>({
    key: storageKey,
    defaultValue: defaultTheme,
  });

  // Skip view transition on initial render to avoid fade-in flash
  const isFirstRender = useRef(true);

  const applyTheme = useCallback((resolvedTheme: "dark" | "light") => {
    const root = document.documentElement;

    // Kill per-element CSS transitions so only the bitmap crossfade animates
    root.classList.add("vt-suppress-transitions");

    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);

    // Sync theme-color meta tag so Android system nav bar matches
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute(
        "content",
        resolvedTheme === "dark" ? "#111113" : "#f5f3ef",
      );
    }

    // Re-enable transitions after the view transition captures the new snapshot
    requestAnimationFrame(() => {
      root.classList.remove("vt-suppress-transitions");
    });
  }, []);

  useEffect(() => {
    const resolved: "dark" | "light" =
      theme === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : theme;

    // Use View Transition API for smooth crossfade (skipped on first paint)
    if (!isFirstRender.current && "startViewTransition" in document) {
      (document as Document & { startViewTransition: (cb: () => void) => void })
        .startViewTransition(() => applyTheme(resolved));
    } else {
      applyTheme(resolved);
    }

    isFirstRender.current = false;
  }, [theme, applyTheme]);

  const value = {
    theme,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
